import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, CreditCard, ArrowLeft, ShieldCheck, CheckCircle2, Loader2, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { createOrder, verifyPayment, getConfig, validateCoupon } from "@/lib/api";
import { getUTM } from "@/lib/utm";
import { TID } from "@/lib/testIds";

const initialAddress = {
  full_name: "",
  phone: "",
  email: "",
  address_line: "",
  city: "",
  state: "",
  pincode: "",
};

export default function PurchaseModal({ open, onOpenChange, initialMethod }) {
  const [step, setStep] = useState("choose"); // choose | form | success
  const [method, setMethod] = useState(initialMethod || null);
  const [address, setAddress] = useState(initialAddress);
  const [submitting, setSubmitting] = useState(false);
  const [confirmedOrderId, setConfirmedOrderId] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [applied, setApplied] = useState(null); // {code,label,discount,total}
  const [couponBusy, setCouponBusy] = useState(false);

  // Reset when modal opens with a preset method
  React.useEffect(() => {
    if (open) {
      setStep(initialMethod ? "form" : "choose");
      setMethod(initialMethod || null);
      setAddress(initialAddress);
      setConfirmedOrderId("");
      setCouponInput("");
      setApplied(null);
    }
  }, [open, initialMethod]);

  const basePrice = method === "prepaid" ? 749 : 799;
  const price = applied ? Math.max(1, basePrice - applied.discount) : basePrice;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return toast.error("Enter a coupon code");
    setCouponBusy(true);
    try {
      const res = await validateCoupon(couponInput.trim(), method, 1);
      setApplied(res);
      toast.success(`Coupon applied: -₹${res.discount}`);
    } catch (e) {
      setApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally {
      setCouponBusy(false);
    }
  };

  const clearCoupon = () => { setApplied(null); setCouponInput(""); };

  const onChange = (k) => (e) => setAddress((a) => ({ ...a, [k]: e.target.value }));

  const validate = () => {
    if (!address.full_name.trim()) return "Please enter your full name";
    if (!/^[6-9]\d{9}$/.test(address.phone)) return "Enter a valid 10-digit phone";
    if (!address.address_line.trim()) return "Please enter your address";
    if (!address.city.trim()) return "Please enter your city";
    if (!address.state.trim()) return "Please enter your state";
    if (!/^\d{6}$/.test(address.pincode)) return "Enter a valid 6-digit pincode";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) return toast.error(err);

    setSubmitting(true);
    try {
      const orderRes = await createOrder({
        payment_method: method,
        quantity: 1,
        address: { ...address, email: address.email || undefined },
        coupon_code: applied?.code || undefined,
        utm: getUTM() || undefined,
      });

      if (method === "cod") {
        setConfirmedOrderId(orderRes.order_id);
        setStep("success");
        toast.success("Order confirmed! We'll call you to verify.");
        return;
      }

      // Prepaid — Razorpay
      const cfg = await getConfig();
      if (!cfg.razorpay_configured || !window.Razorpay) {
        // Placeholder mode — mark as paid via verify endpoint
        await verifyPayment({
          order_id: orderRes.order_id,
          razorpay_order_id: orderRes.razorpay_order_id,
          razorpay_payment_id: `pay_placeholder_${Date.now()}`,
          razorpay_signature: "placeholder",
        });
        setConfirmedOrderId(orderRes.order_id);
        setStep("success");
        toast.success("Order placed! (Razorpay keys not configured — test mode)");
        return;
      }

      const rzp = new window.Razorpay({
        key: cfg.razorpay_key_id,
        amount: orderRes.amount * 100,
        currency: "INR",
        name: "Bajrang Veda",
        description: "Ayurvedic Weight Gain Supplement",
        order_id: orderRes.razorpay_order_id,
        prefill: {
          name: address.full_name,
          contact: address.phone,
          email: address.email || undefined,
        },
        theme: { color: "#FF7A00" },
        handler: async (response) => {
          try {
            await verifyPayment({
              order_id: orderRes.order_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setConfirmedOrderId(orderRes.order_id);
            setStep("success");
            toast.success("Payment successful!");
          } catch (e) {
            toast.error("Payment verification failed. Contact support.");
          }
        },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.modal}
        className="max-h-[92vh] max-w-md overflow-y-auto border-white/10 bg-neutral-950 text-white sm:max-w-lg"
      >
        {step === "choose" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl">Choose Payment</DialogTitle>
              <DialogDescription className="text-neutral-400">
                Pick how you&apos;d like to pay for your Bajrang Veda bottle.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 grid gap-3">
              <button
                data-testid={TID.modalPrepaid}
                onClick={() => { setMethod("prepaid"); setStep("form"); }}
                className="group flex items-center justify-between rounded-2xl border border-[#FF7A00]/50 bg-gradient-to-br from-[#1a0d02] to-black p-4 text-left transition-transform hover:scale-[1.01] glow-orange-sm"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#FFC107]">Best Value · 63% OFF</div>
                  <div className="font-display text-2xl">Pay Online — ₹749</div>
                  <div className="text-xs text-neutral-400">UPI · Cards · Wallets · Netbanking</div>
                </div>
                <CreditCard className="text-[#FF7A00]" />
              </button>
              <button
                data-testid={TID.modalCod}
                onClick={() => { setMethod("cod"); setStep("form"); }}
                className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/5 p-4 text-left transition-transform hover:scale-[1.01]"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">Cash on Delivery</div>
                  <div className="font-display text-2xl">Order COD — ₹799</div>
                  <div className="text-xs text-neutral-500">Pay at your doorstep</div>
                </div>
                <Truck className="text-white" />
              </button>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-3 text-[10px] uppercase tracking-widest text-neutral-500">
                <ShieldCheck size={12} className="text-[#FF7A00]" /> SSL · UPI · Visa · Mastercard · RuPay
              </div>
            </div>
          </>
        )}

        {step === "form" && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button
                  data-testid={TID.modalBack}
                  onClick={() => setStep("choose")}
                  className="rounded-full p-1 text-neutral-400 hover:bg-white/5 hover:text-white"
                >
                  <ArrowLeft size={18} />
                </button>
                <DialogTitle className="font-display text-2xl">
                  {method === "prepaid" ? "Pay Online" : "Cash on Delivery"} · ₹{price}
                </DialogTitle>
              </div>
              <DialogDescription className="text-neutral-400">
                Enter shipping details. Free gifts (₹3000) auto-added.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 grid gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="full_name">Full Name</Label>
                <Input data-testid={TID.modalName} id="full_name" value={address.full_name} onChange={onChange("full_name")} className="border-white/10 bg-white/5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="phone">Phone</Label>
                  <Input data-testid={TID.modalPhone} id="phone" inputMode="numeric" maxLength={10} value={address.phone} onChange={onChange("phone")} className="border-white/10 bg-white/5" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="pincode">Pincode</Label>
                  <Input data-testid={TID.modalPincode} id="pincode" inputMode="numeric" maxLength={6} value={address.pincode} onChange={onChange("pincode")} className="border-white/10 bg-white/5" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="email">Email (optional)</Label>
                <Input data-testid={TID.modalEmail} id="email" type="email" value={address.email} onChange={onChange("email")} className="border-white/10 bg-white/5" />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="address_line">Address</Label>
                <Input data-testid={TID.modalAddress} id="address_line" value={address.address_line} onChange={onChange("address_line")} className="border-white/10 bg-white/5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="city">City</Label>
                  <Input data-testid={TID.modalCity} id="city" value={address.city} onChange={onChange("city")} className="border-white/10 bg-white/5" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="state">State</Label>
                  <Input data-testid={TID.modalState} id="state" value={address.state} onChange={onChange("state")} className="border-white/10 bg-white/5" />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label>Coupon Code</Label>
                {applied ? (
                  <div className="flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-emerald-400" />
                      <span className="font-mono font-semibold text-emerald-300">{applied.code}</span>
                      <span className="text-xs text-emerald-400/80">-₹{applied.discount}</span>
                    </div>
                    <button
                      type="button"
                      onClick={clearCoupon}
                      className="rounded-md p-1 text-neutral-400 hover:bg-white/5 hover:text-white"
                      aria-label="Remove coupon"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      data-testid={TID.modalCoupon}
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="e.g. LAUNCH100"
                      className="border-white/10 bg-white/5 font-mono uppercase"
                    />
                    <button
                      type="button"
                      data-testid={TID.modalCouponApply}
                      disabled={couponBusy}
                      onClick={applyCoupon}
                      className="shrink-0 rounded-md border border-[#FF7A00]/40 bg-[#FF7A00]/10 px-4 text-xs font-bold uppercase tracking-widest text-[#FF7A00] hover:bg-[#FF7A00]/20 disabled:opacity-60"
                    >
                      {couponBusy ? <Loader2 className="animate-spin" size={14} /> : "Apply"}
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-1 space-y-1 rounded-xl bg-white/5 p-3 text-sm">
                <div className="flex items-center justify-between text-neutral-400">
                  <span>Subtotal</span>
                  <span>₹{basePrice}</span>
                </div>
                {applied && (
                  <div className="flex items-center justify-between text-emerald-400">
                    <span>Discount ({applied.code})</span>
                    <span>-₹{applied.discount}</span>
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between border-t border-white/10 pt-2">
                  <span className="text-neutral-400">Total payable</span>
                  <span className="font-display text-2xl text-white">₹{price}</span>
                </div>
              </div>

              <button
                data-testid={TID.modalConfirm}
                disabled={submitting}
                onClick={handleSubmit}
                className="mt-1 flex items-center justify-center gap-2 rounded-full bg-fire py-4 text-sm font-bold uppercase tracking-widest text-black transition-transform hover:scale-[1.01] disabled:opacity-70"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : null}
                {method === "prepaid" ? `Pay ₹${price} Securely` : `Confirm COD Order — ₹${price}`}
              </button>
              <div className="text-center text-[10px] uppercase tracking-widest text-neutral-500">
                SSL Secured · Powered by Razorpay
              </div>
            </div>
          </>
        )}

        {step === "success" && (
          <div data-testid={TID.successBanner} className="py-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-fire text-black">
              <CheckCircle2 size={32} />
            </div>
            <div className="mt-4 font-display text-3xl text-white">Order Confirmed!</div>
            <p className="mt-2 text-sm text-neutral-400">
              Order ID: <span className="text-[#FFC107]">{confirmedOrderId.slice(0, 12)}</span>
            </p>
            <p className="mt-3 text-sm text-neutral-300">
              We&apos;ll ship your Bajrang Veda bottle within 24 hours. Your free
              diet plan & virtual trainer will be sent to your phone shortly.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-6 rounded-full bg-fire px-8 py-3 text-sm font-bold uppercase tracking-widest text-black"
            >
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
